import React, { Component } from 'react';
import { X, Send, Reply, Paperclip, Image, Video, File } from 'lucide-react';
import { UiStyles } from '@/theme/ui';
import type { ChatMessage } from '@/data/mockTableBookingData'; // type only; chat always uses the live API (no mock fallback)
import { apiService } from '@/services/api';
import TranslateInline, { TranslateBar } from '@/components/TranslateInline';
import { getInputLang } from '@/utils/translatePref';
import VoiceInput from '@/components/VoiceInput/VoiceInput';
import { bookingChatWebSocketService } from '@/services/bookingChatWebSocket';
import { cleanUUID, isValidUUID } from '@/utils/uuid';
import styles from './BookingChat.module.scss';

interface BookingChatProps {
  organizerName: string;
  branchName: string;
  bookingId: string; // This is the actual booking ID (NOT table ID)
  branchId?: string; // Needed to gate AI translation per branch (feature hidden without it)
  onClose: () => void;
  /** Table-hub mode: render inline (no overlay/own header/close) with an action toolbar + filter. */
  embedded?: boolean;
  /** Actions surfaced in the hub toolbar (launch the real Order/Menu/Bill/Call panels). */
  hubActions?: {
    onCall?: () => void;
    onOrder?: () => void;
    onMenu?: () => void;
    onBill?: () => void;
    callUnread?: number;
    menuLabel?: string;
  };
}

type HubFilter = 'all' | 'calls' | 'orders' | 'bill';

interface ReplyToMessage {
  id: string;
  messageText: string;
  senderType: 'customer' | 'organizer';
  senderId: string;
  createdAt: string;
}

interface BookingMessageAttachment {
  id: string;
  attachmentType: 'image' | 'video' | 'file' | 'youtube' | 'facebook' | 'twitter' | 'instagram' | 'link';
  fileUrl: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  thumbnailUrl?: string;
  linkUrl?: string;
  metadata?: {
    videoId?: string;
    postId?: string;
    embedUrl?: string;
    thumbnailUrl?: string;
    [key: string]: any;
  };
  createdAt: string;
}

interface BookingChatMessage {
  id: string;
  senderId: string;
  senderType: 'customer' | 'organizer' | 'system';
  senderName: string;
  message: string;
  timestamp: string;
  read: boolean;
  readAt?: string;
  /** 'call_event' = a Call-staff request event; 'call_message' = a mirrored call clarification. */
  messageType?: string | null;
  /** Language the sender typed in (from message metadata) — a hint for accurate translation. */
  sourceLang?: string;
  replyToMessage?: ReplyToMessage | null;
  attachments?: BookingMessageAttachment[];
  // Set when an optimistic send failed so the bubble stays visible (dimmed) with a retry affordance
  // instead of being silently dropped. Absent = normal (pending temp- id or delivered) message.
  status?: 'failed';
}

interface BookingChatState {
  messages: BookingChatMessage[];
  inputMessage: string;
  isLoading: boolean;
  isSending: boolean;
  isTyping: boolean;
  organizerTyping: boolean;
  error: string | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  chatEnabled: boolean;
  bookingStatus: string | null;
  isLoadingStatus: boolean;
  replyingTo: BookingChatMessage | null;
  selectedFiles: File[];
  isUploading: boolean;
  translationAvailable: boolean;
  translationBranchId?: string; // branchId used for translation (prop, or learned from chat-status)
  hubFilter: HubFilter; // timeline filter in hub mode
}

class BookingChat extends Component<BookingChatProps, BookingChatState> {
  private messagesEndRef: React.RefObject<HTMLDivElement>;
  private chatContainerRef: React.RefObject<HTMLDivElement>;
  private fileInputRef: React.RefObject<HTMLInputElement>;
  private rootRef: React.RefObject<HTMLDivElement>;

  private typingTimeout: NodeJS.Timeout | null = null;
  // Poll fallback: while the chat is open, re-fetch messages so a missed Ably/socket event (e.g. a
  // reconnect gap) doesn't silently drop an incoming message. Additive — the socket path is untouched.
  private messagePollInterval: ReturnType<typeof setInterval> | null = null;
  private prevBodyOverflow = '';

  constructor(props: BookingChatProps) {
    super(props);
    this.state = {
      messages: [],
      inputMessage: '',
      isLoading: false,
      isSending: false,
      isTyping: false,
      organizerTyping: false,
      error: null,
      connectionStatus: 'connecting',
      chatEnabled: false,
      bookingStatus: null,
      isLoadingStatus: true,
      replyingTo: null,
      selectedFiles: [],
      isUploading: false,
      translationAvailable: false,
      hubFilter: 'all'
    };
    this.messagesEndRef = React.createRef();
    this.chatContainerRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.rootRef = React.createRef();
  }

  // On mobile the chat is fullscreen and `position: fixed`, which is sized to the LAYOUT viewport —
  // so the on-screen keyboard overlaps the bottom input. Size the chat to the VISUAL viewport instead
  // (which shrinks when the keyboard opens) so the input always rides just above the keyboard, like a
  // native app. No-op on desktop widths (inline styles cleared so the CSS panel size applies).
  private syncViewport = () => {
    const el = this.rootRef.current;
    const vv = window.visualViewport;
    if (!el) return;
    if (!vv || window.innerWidth > 768) {
      el.style.height = '';
      el.style.top = '';
      el.style.bottom = '';
      return;
    }
    el.style.height = `${vv.height}px`;
    el.style.top = `${vv.offsetTop}px`;
    el.style.bottom = 'auto';
    // Keep the latest messages in view as the keyboard reflows the panel.
    this.scrollToBottom();
  };

  private setupViewportHandling = () => {
    const vv = window.visualViewport;
    if (!vv) return;
    vv.addEventListener('resize', this.syncViewport);
    vv.addEventListener('scroll', this.syncViewport);
    this.syncViewport();
  };

  private teardownViewportHandling = () => {
    const vv = window.visualViewport;
    if (!vv) return;
    vv.removeEventListener('resize', this.syncViewport);
    vv.removeEventListener('scroll', this.syncViewport);
  };

  componentDidMount() {
    console.log('[BookingChat] Component mounted with props:', {
      organizerName: this.props.organizerName,
      branchName: this.props.branchName,
      bookingId: this.props.bookingId
    });

    // Is AI translation available for THIS branch? Uses the branchId prop if given, else the one
    // learned from the chat-status call (see loadStatus). Without any branchId, stays hidden.
    this.checkTranslationAvailability(this.props.branchId);
    
    if (!this.props.bookingId) {
      console.error('[BookingChat] No booking ID provided!');
      this.setState({ 
        error: 'Booking ID is missing. Cannot initialize chat.',
        isLoadingStatus: false
      });
      return;
    }

    // Clean and validate booking ID (remove any prefixes)
    const cleanedBookingId = cleanUUID(this.props.bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) {
      console.error('[BookingChat] Invalid booking ID format:', this.props.bookingId);
      this.setState({ 
        error: 'Invalid booking ID format. Please use a valid UUID.',
        isLoadingStatus: false
      });
      return;
    }

    // Check chat status first
    this.checkChatStatus();

    // Keep the input above the mobile keyboard.
    this.setupViewportHandling();

    // As a full-screen modal (not embedded hub), lock the page behind so mobile scrolling doesn't
    // move the background.
    if (!this.props.embedded) {
      this.prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
  }

  // Fetch AI-translation availability for a branch and reveal the 🌐 Translate affordance if allowed.
  checkTranslationAvailability = (branchId?: string) => {
    if (!branchId || this.state.translationAvailable) return;
    apiService.getTranslationAvailableForBranch(branchId)
      .then((available) => { if (available) this.setState({ translationAvailable: true, translationBranchId: branchId }); })
      .catch(() => { /* stays hidden */ });
  };

  checkChatStatus = async () => {
    const { bookingId } = this.props;
    
    // Clean booking ID to ensure it's a valid UUID
    const cleanedBookingId = cleanUUID(bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) {
      this.setState({ 
        isLoadingStatus: false,
        error: 'Invalid booking ID format.'
      });
      return;
    }
    
    console.log('[BookingChat] Checking chat status for booking ID:', cleanedBookingId);
    this.setState({ isLoadingStatus: true });

    try {
      const response = await apiService.getBookingChatStatus(cleanedBookingId);
      
      if (response && response.success && response.data) {
        // API returns "enabled" instead of "chatEnabled"
        const { enabled, bookingStatus, message } = response.data;

        // If no branchId prop was passed, learn it from the status response to gate translation.
        if (!this.props.branchId && (response.data as any).branchId) {
          this.checkTranslationAvailability((response.data as any).branchId);
        }

        console.log('[BookingChat] Chat status:', { enabled, bookingStatus });
        
        this.setState({
          chatEnabled: enabled, // Map "enabled" to "chatEnabled" for state
          bookingStatus,
          isLoadingStatus: false
        });

        // Only initialize chat if enabled
        if (enabled) {
          this.loadMessages();
          this.setupSocketListeners();
          this.startMessagePolling();
          this.scrollToBottom();
        } else {
          this.setState({
            error: message || 'Chat will be enabled once the organizer accepts your booking request.'
          });
        }
      } else {
        throw new Error(response?.message || 'Failed to check chat status');
      }
    } catch (error: any) {
      console.error('[BookingChat] Failed to check chat status:', error);
      this.setState({
        isLoadingStatus: false,
        error: error?.message || 'Failed to check chat status. Please try again.'
      });
    }
  };

  componentWillUnmount() {
    this.removeSocketListeners();
    this.teardownViewportHandling();
    if (!this.props.embedded) document.body.style.overflow = this.prevBodyOverflow || '';
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    this.stopMessagePolling();
    // Don't disconnect - keep connection for other bookings
  }

  // Start/stop the ~5s message poll (safety net for a dropped socket). Idempotent start.
  startMessagePolling = () => {
    if (this.messagePollInterval) return;
    this.messagePollInterval = setInterval(() => { this.pollMessages(); }, 5000);
  };

  stopMessagePolling = () => {
    if (this.messagePollInterval) {
      clearInterval(this.messagePollInterval);
      this.messagePollInterval = null;
    }
  };

  // Re-fetch this booking's messages (same API call loadMessages uses) and merge by id — append
  // only messages we don't already have, then re-sort by timestamp. Never clobbers optimistic/temp
  // messages or existing state, so it's safe to run alongside the socket. Non-fatal on error.
  pollMessages = async () => {
    const cleanedBookingId = cleanUUID(this.props.bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) return;
    try {
      const response = await apiService.getBookingChatMessages(cleanedBookingId, 1, 50);
      if (!response || !response.success || !response.data || !response.data.messages) return;
      const apiMessages: BookingChatMessage[] = response.data.messages.map((msg: any) => ({
        id: msg.id,
        senderId: msg.senderId || msg.sender?.id,
        senderType: msg.senderType || (msg.sender?.id ? 'organizer' : 'customer'),
        senderName: msg.senderName || msg.sender?.name || (msg.senderType === 'customer' ? 'You' : this.props.organizerName),
        message: msg.messageText || msg.message || '', messageType: msg.messageType || null, sourceLang: msg.metadata?.sourceLang || undefined,
        timestamp: msg.createdAt || msg.timestamp,
        read: msg.isRead || false,
        readAt: msg.readAt,
        replyToMessage: msg.replyToMessage ? {
          id: msg.replyToMessage.id,
          messageText: msg.replyToMessage.messageText,
          senderType: msg.replyToMessage.senderType,
          senderId: msg.replyToMessage.senderId,
          createdAt: msg.replyToMessage.createdAt
        } : null,
        attachments: msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 ? msg.attachments.map((att: any) => ({
          id: att.id,
          attachmentType: att.attachmentType,
          fileUrl: att.fileUrl,
          fileName: att.fileName,
          fileSize: att.fileSize,
          mimeType: att.mimeType,
          thumbnailUrl: att.thumbnailUrl,
          linkUrl: att.linkUrl,
          metadata: att.metadata,
          createdAt: att.createdAt
        })) : undefined
      }));

      this.setState(prevState => {
        const existingIds = new Set(prevState.messages.map(m => m.id));
        const newOnes = apiMessages.filter(m => m.id && !existingIds.has(m.id));
        if (newOnes.length === 0) return null;
        const merged = [...prevState.messages, ...newOnes].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        return { ...prevState, messages: merged };
      });
    } catch {
      // Non-fatal: live socket still primary; chat works over REST.
    }
  };

  componentDidUpdate(_prevProps: BookingChatProps, prevState: BookingChatState) {
    // Only scroll on a NEW message — not on every state change. Scrolling on each keystroke
    // (input typing updates state) is what made the input jump up/down on mobile keyboards.
    if (prevState.messages.length !== this.state.messages.length) {
      this.scrollToBottom();
    }
  }

  setupSocketListeners = () => {
    const { bookingId } = this.props;
    
    // Clean booking ID to ensure it's a valid UUID
    const cleanedBookingId = cleanUUID(bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) {
      console.error('[BookingChat] Invalid booking ID for socket connection:', bookingId);
      return;
    }
    
    console.log('[BookingChat] Connecting to socket with booking ID:', cleanedBookingId);
    // Connect to booking chat room (using booking ID)
    bookingChatWebSocketService.connect(cleanedBookingId);

    // Listen for connection status
    bookingChatWebSocketService.on('booking_chat:joined', () => {
      this.setState({ connectionStatus: 'connected' });
    });

    // Listen for new messages
    bookingChatWebSocketService.on('booking_chat:message', this.handleNewMessage);
    
    // Listen for message sent confirmation
    bookingChatWebSocketService.on('booking_chat:message_sent', this.handleMessageSent);
    
    // Listen for typing indicators
    bookingChatWebSocketService.on('booking_chat:typing', this.handleTyping);
    
    // Listen for read receipts
    bookingChatWebSocketService.on('booking_chat:read', this.handleMessagesRead);
    
    // Listen for booking message notifications (even when chat is closed)
    bookingChatWebSocketService.on('booking_message:notification', this.handleNotification);
    
    // Listen for errors
    bookingChatWebSocketService.on('error', this.handleSocketError);
    bookingChatWebSocketService.on('disconnect', () => {
      this.setState({ connectionStatus: 'disconnected' });
    });
  };

  removeSocketListeners = () => {
    const { bookingId } = this.props;
    bookingChatWebSocketService.off('booking_chat:joined', () => {});
    bookingChatWebSocketService.off('booking_chat:message', this.handleNewMessage);
    bookingChatWebSocketService.off('booking_chat:message_sent', this.handleMessageSent);
    bookingChatWebSocketService.off('booking_chat:typing', this.handleTyping);
    bookingChatWebSocketService.off('booking_chat:read', this.handleMessagesRead);
    bookingChatWebSocketService.off('booking_message:notification', this.handleNotification);
    bookingChatWebSocketService.off('error', this.handleSocketError);
    // Leave booking chat room when unmounting
    if (bookingId) {
      const cleanedBookingId = cleanUUID(bookingId);
      if (cleanedBookingId && isValidUUID(cleanedBookingId)) {
        bookingChatWebSocketService.leaveBookingChat(cleanedBookingId);
      }
    }
  };

  handleNewMessage = (data: any) => {
    console.log('[BookingChat] New message received:', data);
    
    // Log attachments for debugging
    if (data.attachments && Array.isArray(data.attachments) && data.attachments.length > 0) {
      console.log('[BookingChat] Message has attachments:', {
        messageId: data.id,
        attachments: data.attachments,
        attachmentTypes: data.attachments.map((a: any) => a.attachmentType),
        imageAttachments: data.attachments.filter((a: any) => a.attachmentType === 'image')
      });
    }
    
    // Clean booking IDs for comparison
    const cleanedDataBookingId = cleanUUID(data.bookingId);
    const cleanedPropsBookingId = cleanUUID(this.props.bookingId);
    
    // Only process messages for this booking
    if (cleanedDataBookingId === cleanedPropsBookingId || data.bookingId === this.props.bookingId) {
      const messageId = data.id;
      
      if (!messageId) {
        console.warn('[BookingChat] Message received without ID, skipping:', data);
        return;
      }
      
      // Check if message already exists to prevent duplicates
      this.setState(prevState => {
        // Check by ID first
        const messageExistsById = prevState.messages.some(msg => msg.id === messageId);
        if (messageExistsById) {
          console.log('[BookingChat] Message already exists by ID, skipping duplicate:', messageId);
          return prevState; // Return previous state unchanged
        }
        
        // For customer messages, also check if we have a recent message with same content
        // This prevents duplicates when socket event arrives for a message we just sent
        if (data.senderType === 'customer') {
          const messageText = data.messageText || data.message || '';
          const recentCustomerMessage = prevState.messages
            .filter(msg => msg.senderType === 'customer')
            .slice(-5) // Check last 5 customer messages
            .find(msg => {
              // Check if message text matches and was sent recently (within last 10 seconds)
              const timeDiff = Math.abs(new Date(msg.timestamp).getTime() - new Date(data.createdAt || data.timestamp || Date.now()).getTime());
              return msg.message === messageText && timeDiff < 10000;
            });
          
          if (recentCustomerMessage) {
            console.log('[BookingChat] Duplicate customer message detected (same content), skipping:', {
              socketMessageId: messageId,
              existingMessageId: recentCustomerMessage.id,
              messageText
            });
            // Update the existing message with the real ID if it's still a temp message
            if (recentCustomerMessage.id.startsWith('temp-')) {
              return {
                ...prevState,
                messages: prevState.messages.map(msg =>
                  msg.id === recentCustomerMessage.id
                    ? { ...msg, id: messageId }
                    : msg
                )
              };
            }
            return prevState;
          }
        }
        
        const newMessage: BookingChatMessage = {
          id: messageId,
          senderId: data.senderId || data.sender?.id || 'organizer-1',
          senderType: data.senderType || 'organizer',
          senderName: data.senderName || data.sender?.name || this.props.organizerName,
          message: data.messageText || data.message || '',
          messageType: data.messageType || null, sourceLang: data.metadata?.sourceLang || undefined,
          timestamp: data.createdAt || data.timestamp || new Date().toISOString(),
          read: data.isRead || false,
          readAt: data.readAt,
          replyToMessage: data.replyToMessage ? {
            id: data.replyToMessage.id,
            messageText: data.replyToMessage.messageText,
            senderType: data.replyToMessage.senderType,
            senderId: data.replyToMessage.senderId,
            createdAt: data.replyToMessage.createdAt
          } : null,
          attachments: data.attachments && Array.isArray(data.attachments) && data.attachments.length > 0 ? data.attachments.map((att: any) => ({
            id: att.id,
            attachmentType: att.attachmentType,
            fileUrl: att.fileUrl,
            fileName: att.fileName,
            fileSize: att.fileSize,
            mimeType: att.mimeType,
            thumbnailUrl: att.thumbnailUrl,
            linkUrl: att.linkUrl,
            metadata: att.metadata,
            createdAt: att.createdAt
          })) : undefined
        };

        // Mark as read if it's from organizer and chat is open
        if (newMessage.senderType === 'organizer') {
          // Mark as read asynchronously to avoid blocking state update
          setTimeout(() => {
            this.markMessagesAsRead([newMessage.id]);
          }, 100);
        }

        // Add message to the end (newest at bottom) and sort by timestamp to ensure correct order
        const updatedMessages = [...prevState.messages, newMessage].sort((a, b) => {
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        });

        return {
          ...prevState,
          messages: updatedMessages,
          organizerTyping: false
        };
      });
    } else {
      console.log('[BookingChat] Message booking ID mismatch:', {
        received: data.bookingId,
        expected: this.props.bookingId,
        cleanedReceived: cleanedDataBookingId,
        cleanedExpected: cleanedPropsBookingId
      });
    }
  };

  handleMessageSent = (data: any) => {
    console.log('[BookingChat] Message sent confirmation:', data);
    // Update message status if needed
    if (data.id) {
      this.setState(prevState => ({
        messages: prevState.messages.map(msg =>
          msg.id === data.tempId ? { ...msg, id: data.id } : msg
        ),
        isSending: false
      }));
    }
  };

  handleTyping = (data: any) => {
    if (data.bookingId === this.props.bookingId && data.senderType === 'organizer') {
      this.setState({ organizerTyping: data.isTyping });
      
      // Auto-hide typing indicator after 3 seconds
      if (data.isTyping) {
        setTimeout(() => {
          this.setState({ organizerTyping: false });
        }, 3000);
      }
    }
  };

  handleMessagesRead = (data: any) => {
    if (data.bookingId === this.props.bookingId && data.messageIds) {
      this.setState(prevState => ({
        messages: prevState.messages.map(msg =>
          data.messageIds.includes(msg.id) ? { ...msg, read: true, readAt: data.readAt } : msg
        )
      }));
    }
  };

  handleNotification = (notification: any) => {
    // Handle booking message notification (works even when chat is closed)
    // This can be used to show notification badges, browser notifications, etc.
    console.log('[BookingChat] Booking message notification received:', notification);
    
    // Only process notifications for this booking
    const cleanedNotificationBookingId = cleanUUID(notification.bookingId);
    const cleanedPropsBookingId = cleanUUID(this.props.bookingId);
    
    if (cleanedNotificationBookingId === cleanedPropsBookingId || notification.bookingId === this.props.bookingId) {
      // If chat is open, we might want to show a subtle notification
      // If chat is closed, this would typically trigger a badge or browser notification
      // For now, we'll just log it - you can extend this to show UI notifications
      
      // Example: Show browser notification if permission granted
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        const senderName = notification.organizer?.name || notification.customer?.name || 'Someone';
        new Notification(`New message from ${senderName}`, {
          body: notification.message?.preview || notification.message?.text || 'You have a new message',
          icon: notification.organizer?.profilePic || notification.customer?.profilePic
        });
      }
    }
  };

  handleSocketError = (error: any) => {
    console.error('[BookingChat] Socket error:', error);
    this.setState({ 
      connectionStatus: 'error',
      error: 'Connection error. Please refresh the page.'
    });
  };

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  loadMessages = async () => {
    const { bookingId } = this.props;
    
    // Clean booking ID to ensure it's a valid UUID
    const cleanedBookingId = cleanUUID(bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) {
      this.setState({ 
        isLoading: false,
        error: 'Invalid booking ID format.'
      });
      return;
    }
    
    console.log('[BookingChat] Loading messages for booking ID:', cleanedBookingId);
    this.setState({ isLoading: true, error: null });

    try {
      // Load messages from API using booking ID
      const response = await apiService.getBookingChatMessages(cleanedBookingId, 1, 50);
      
      if (response && response.success && response.data && response.data.messages) {
        // Messages are returned in chronological order (oldest first) - keep this order (newest at bottom)
        const apiMessages: BookingChatMessage[] = response.data.messages
          .map((msg: any) => {
            // Log attachments for debugging
            if (msg.attachments && msg.attachments.length > 0) {
              console.log('[BookingChat] Message has attachments:', {
                messageId: msg.id,
                attachments: msg.attachments,
                attachmentTypes: msg.attachments.map((a: any) => a.attachmentType)
              });
            }
            
            return {
              id: msg.id,
              senderId: msg.senderId || msg.sender?.id,
              senderType: msg.senderType || (msg.sender?.id ? 'organizer' : 'customer'),
              senderName: msg.senderName || msg.sender?.name || (msg.senderType === 'customer' ? 'You' : this.props.organizerName),
              message: msg.messageText || msg.message || '', messageType: msg.messageType || null, sourceLang: msg.metadata?.sourceLang || undefined,
              timestamp: msg.createdAt || msg.timestamp,
              read: msg.isRead || false,
              readAt: msg.readAt,
              replyToMessage: msg.replyToMessage ? {
                id: msg.replyToMessage.id,
                messageText: msg.replyToMessage.messageText,
                senderType: msg.replyToMessage.senderType,
                senderId: msg.replyToMessage.senderId,
                createdAt: msg.replyToMessage.createdAt
              } : null,
              attachments: msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 ? msg.attachments.map((att: any) => ({
                id: att.id,
                attachmentType: att.attachmentType,
                fileUrl: att.fileUrl,
                fileName: att.fileName,
                fileSize: att.fileSize,
                mimeType: att.mimeType,
                thumbnailUrl: att.thumbnailUrl,
                linkUrl: att.linkUrl,
                metadata: att.metadata,
                createdAt: att.createdAt
              })) : undefined
            };
          })
          // Sort by timestamp ascending (oldest first, newest last - newest at bottom)
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        this.setState({ 
          messages: apiMessages,
          isLoading: false 
        });

        // Mark messages as read when loading (auto-mark organizer messages as read)
        if (apiMessages.length > 0) {
          const unreadIds = apiMessages
            .filter(msg => msg.senderType === 'organizer' && !msg.read)
            .map(msg => msg.id);
          
          if (unreadIds.length > 0) {
            this.markMessagesAsRead(unreadIds);
          }
        }
      } else {
        throw new Error(response?.message || 'Failed to load messages');
      }
    } catch (error: any) {
      console.error('[BookingChat] Failed to load messages from API:', error);
      
      // Show error but don't use mock data - chat should work with real API
      this.setState({ 
        messages: [],
        isLoading: false,
        error: error?.message || 'Failed to load messages. Please try again.'
      });
    }
  };

  markMessagesAsRead = async (messageIds: string[]) => {
    const { bookingId } = this.props;
    
    // Clean booking ID to ensure it's a valid UUID
    const cleanedBookingId = cleanUUID(bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) {
      console.error('[BookingChat] Invalid booking ID for marking as read:', bookingId);
      return;
    }
    
    try {
      await apiService.markBookingChatMessagesAsRead(cleanedBookingId, messageIds);
    } catch (error) {
      console.error('[BookingChat] Failed to mark messages as read:', error);
    }
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { bookingId } = this.props;
    const value = e.target.value;
    
    // Clean booking ID
    const cleanedBookingId = cleanUUID(bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) {
      return;
    }
    
    this.setState({ inputMessage: value });

    // Send typing indicator using new API
    if (value.trim().length > 0) {
      bookingChatWebSocketService.startTyping(cleanedBookingId, 'customer');
      
      // Clear previous timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
      
      // Stop typing indicator after 3 seconds of no input (per documentation)
      this.typingTimeout = setTimeout(() => {
        bookingChatWebSocketService.stopTyping(cleanedBookingId, 'customer');
        this.setState({ isTyping: false });
      }, 3000);
    } else {
      bookingChatWebSocketService.stopTyping(cleanedBookingId, 'customer');
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
    }
  };

  handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const { inputMessage } = this.state;
    const { bookingId } = this.props;
    
    // Clean booking ID to ensure it's a valid UUID
    const cleanedBookingId = cleanUUID(bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) {
      this.setState({ 
        error: 'Invalid booking ID format. Cannot send message.'
      });
      return;
    }
    
    console.log('[BookingChat] Sending message for booking ID:', cleanedBookingId);
    
    // Allow sending if there's text OR files
    if ((!inputMessage.trim() && this.state.selectedFiles.length === 0) || this.state.isSending) return;

    const messageText = inputMessage.trim() || ''; // Allow empty text if files are present
    const tempId = `temp-${Date.now()}`;

    // Create optimistic message (attachments will be added after upload)
    const optimisticMessage: BookingChatMessage = {
      id: tempId,
      senderId: 'customer',
      senderType: 'customer',
      senderName: 'You',
      message: messageText,
      timestamp: new Date().toISOString(),
      read: false,
      replyToMessage: this.state.replyingTo && (this.state.replyingTo.senderType === 'customer' || this.state.replyingTo.senderType === 'organizer') ? {
        id: this.state.replyingTo.id,
        messageText: this.state.replyingTo.message,
        senderType: this.state.replyingTo.senderType as 'customer' | 'organizer',
        senderId: this.state.replyingTo.senderId,
        createdAt: this.state.replyingTo.timestamp
      } : null,
      attachments: this.state.selectedFiles.length > 0 ? this.state.selectedFiles.map((file, index) => ({
        id: `temp-attachment-${tempId}-${index}`,
        attachmentType: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file',
        fileUrl: URL.createObjectURL(file), // Temporary local URL for preview
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        createdAt: new Date().toISOString()
      })) : undefined
    };

    // Add message optimistically
    this.setState(prevState => ({
      messages: [...prevState.messages, optimisticMessage],
      inputMessage: '',
      isSending: true,
      isTyping: false
      // Don't clear replyingTo or selectedFiles here - they'll be cleared after successful send
    }));

    // Stop typing indicator
    bookingChatWebSocketService.stopTyping(cleanedBookingId, 'customer');
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    try {
      // Send via REST API using booking ID (messages are sent via API, socket is only for receiving)
      const replyToMessageId = this.state.replyingTo?.id;
      const selectedFiles = this.state.selectedFiles;
      
      this.setState({ isUploading: selectedFiles.length > 0 });
      
      const response = await apiService.sendBookingChatMessage(
        cleanedBookingId,
        messageText,
        replyToMessageId,
        selectedFiles.length > 0 ? selectedFiles : undefined,
        getInputLang(this.props.bookingId) || undefined
      );
      
      if (response && response.success && response.data) {
        // Update message with real ID from API, reply information, and attachments
        this.setState(prevState => ({
          messages: prevState.messages.map(msg =>
            msg.id === tempId
              ? {
                  ...msg,
                  id: response.data.id,
                  timestamp: response.data.createdAt || msg.timestamp,
                  replyToMessage: response.data.replyToMessage ? {
                    id: response.data.replyToMessage.id,
                    messageText: response.data.replyToMessage.messageText,
                    senderType: response.data.replyToMessage.senderType,
                    senderId: response.data.replyToMessage.senderId,
                    createdAt: response.data.replyToMessage.createdAt
                  } : null,
                  attachments: response.data.attachments ? response.data.attachments.map((att: any) => ({
                    id: att.id,
                    attachmentType: att.attachmentType,
                    fileUrl: att.fileUrl,
                    fileName: att.fileName,
                    fileSize: att.fileSize,
                    mimeType: att.mimeType,
                    thumbnailUrl: att.thumbnailUrl,
                    linkUrl: att.linkUrl,
                    metadata: att.metadata,
                    createdAt: att.createdAt
                  })) : undefined
                }
              : msg
          ),
          isSending: false,
          isUploading: false,
          replyingTo: null, // Clear reply state after sending
          selectedFiles: [] // Clear selected files after sending
        }));
        
        // Clear file input
        if (this.fileInputRef.current) {
          this.fileInputRef.current.value = '';
        }
      } else {
        this.setState({ isSending: false, isUploading: false, replyingTo: null });
      }
    } catch (error: any) {
      console.error('[BookingChat] Failed to send message:', error);

      // Keep the optimistic message (so the typed text is NOT lost) but flag it as failed so it
      // renders dimmed with a "Tap to retry" affordance the customer can use to resend.
      this.setState(prevState => ({
        messages: prevState.messages.map(msg =>
          msg.id === tempId ? { ...msg, status: 'failed' as const } : msg
        ),
        isSending: false,
        isUploading: false,
        error: error?.message || 'Failed to send message. Please try again.'
      }));

      // Show error temporarily
      setTimeout(() => {
        this.setState({ error: null });
      }, 5000);
    }
  };

  // Re-send a message whose optimistic send previously failed. Keeps the same (temp-) bubble,
  // flips it back to pending while the request is in flight, then swaps in the real server id on
  // success or re-flags it 'failed' on error. Text (and reply target) are preserved, so nothing is
  // lost. Attachments are not re-uploaded here (their File objects aren't retained on the bubble).
  handleRetryMessage = async (failedMessage: BookingChatMessage) => {
    const cleanedBookingId = cleanUUID(this.props.bookingId);
    if (!cleanedBookingId || !isValidUUID(cleanedBookingId)) return;
    if (this.state.isSending) return;

    const messageText = failedMessage.message;
    const replyToMessageId = failedMessage.replyToMessage?.id;

    // Flip the failed bubble back to pending (clears the failed flag → shows the ⏳ indicator).
    this.setState(prevState => ({
      messages: prevState.messages.map(msg =>
        msg.id === failedMessage.id ? { ...msg, status: undefined } : msg
      ),
      isSending: true,
      error: null
    }));

    try {
      const response = await apiService.sendBookingChatMessage(
        cleanedBookingId,
        messageText,
        replyToMessageId,
        undefined,
        getInputLang(this.props.bookingId) || undefined
      );

      if (response && response.success && response.data) {
        // Success: swap the temp id for the real server id (same as the happy path).
        this.setState(prevState => ({
          messages: prevState.messages.map(msg =>
            msg.id === failedMessage.id
              ? { ...msg, id: response.data.id, status: undefined, timestamp: response.data.createdAt || msg.timestamp }
              : msg
          ),
          isSending: false
        }));
      } else {
        this.setState(prevState => ({
          messages: prevState.messages.map(msg =>
            msg.id === failedMessage.id ? { ...msg, status: 'failed' as const } : msg
          ),
          isSending: false
        }));
      }
    } catch (error: any) {
      console.error('[BookingChat] Failed to retry message:', error);
      this.setState(prevState => ({
        messages: prevState.messages.map(msg =>
          msg.id === failedMessage.id ? { ...msg, status: 'failed' as const } : msg
        ),
        isSending: false,
        error: error?.message || 'Failed to send message. Please try again.'
      }));
      setTimeout(() => this.setState({ error: null }), 5000);
    }
  };

  formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  handleReplyClick = (message: BookingChatMessage) => {
    this.setState({ replyingTo: message });
    // Focus input field
    setTimeout(() => {
      const input = document.querySelector(`.${styles.inputField}`) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  };

  handleCancelReply = () => {
    this.setState({ replyingTo: null });
  };

  scrollToMessage = (messageId: string) => {
    // Find the message element and scroll to it
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight briefly
      messageElement.classList.add(styles.messageHighlight);
      setTimeout(() => {
        messageElement.classList.remove(styles.messageHighlight);
      }, 2000);
    }
  };

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      
      // Validate file size (50MB max per file)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      
      files.forEach(file => {
        if (file.size > maxSize) {
          invalidFiles.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        } else {
          validFiles.push(file);
        }
      });
      
      if (invalidFiles.length > 0) {
        this.setState({
          error: `Some files are too large (max 50MB): ${invalidFiles.join(', ')}`
        });
        setTimeout(() => this.setState({ error: null }), 5000);
      }
      
      // Limit to 10 files total
      this.setState(prevState => {
        const currentFiles = [...prevState.selectedFiles, ...validFiles];
        const limitedFiles = currentFiles.slice(0, 10);
        
        if (currentFiles.length > 10) {
          this.setState({
            error: 'Maximum 10 files allowed. Only first 10 files will be sent.'
          });
          setTimeout(() => this.setState({ error: null }), 5000);
        }
        
        return { selectedFiles: limitedFiles };
      });
    }
  };

  handleRemoveFile = (index: number) => {
    this.setState(prevState => ({
      selectedFiles: prevState.selectedFiles.filter((_, i) => i !== index)
    }));
  };

  handleAttachClick = () => {
    this.fileInputRef.current?.click();
  };

  formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  getFileIcon = (file: File): React.ReactNode => {
    if (file.type.startsWith('image/')) {
      return <Image size={16} />;
    } else if (file.type.startsWith('video/')) {
      return <Video size={16} />;
    } else {
      return <File size={16} />;
    }
  };

  renderAttachment = (attachment: BookingMessageAttachment): React.ReactNode => {
    switch (attachment.attachmentType) {
      case 'image':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <div className={styles.attachmentImage}>
              <img 
                src={attachment.fileUrl} 
                alt={attachment.fileName || 'Image'} 
                onClick={() => window.open(attachment.fileUrl, '_blank')}
                onError={(e) => {
                  console.error('[BookingChat] Image failed to load:', attachment.fileUrl);
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  // Show error message
                  const parent = target.parentElement;
                  if (parent) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = styles.imageError;
                    errorDiv.textContent = 'Failed to load image';
                    parent.appendChild(errorDiv);
                  }
                }}
                loading="lazy"
              />
              {attachment.fileName && (
                <div className={styles.attachmentFileName}>{attachment.fileName}</div>
              )}
            </div>
          </div>
        );

      case 'video':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <div className={styles.attachmentVideo}>
              <video 
                controls 
                preload="metadata"
                playsInline
                muted={false}
                crossOrigin="anonymous"
                poster={attachment.thumbnailUrl || undefined}
                onLoadedMetadata={(e) => {
                  // Ensure audio is enabled when metadata loads
                  const video = e.currentTarget;
                  video.muted = false;
                  video.volume = 1.0;
                  console.log('[BookingChat] Video metadata loaded:', {
                    duration: video.duration,
                    muted: video.muted,
                    volume: video.volume,
                    readyState: video.readyState
                  });
                }}
                onPlay={(e) => {
                  // CRITICAL: Ensure audio is enabled when user plays video
                  const video = e.currentTarget;
                  // Force unmute and set volume to max
                  video.muted = false;
                  video.volume = 1.0;
                  
                  // Try to play with audio enabled
                  if (video.paused) {
                    video.play().catch(err => {
                      console.error('[BookingChat] Error playing video:', err);
                    });
                  }
                  
                  console.log('[BookingChat] Video playing:', {
                    muted: video.muted,
                    volume: video.volume,
                    paused: video.paused,
                    currentTime: video.currentTime
                  });
                }}
                onVolumeChange={(e) => {
                  // Log volume changes for debugging
                  const video = e.currentTarget;
                  console.log('[BookingChat] Video volume changed:', {
                    muted: video.muted,
                    volume: video.volume
                  });
                  
                  // If user unmutes, ensure volume is set
                  if (!video.muted && video.volume === 0) {
                    video.volume = 1.0;
                  }
                }}
                onCanPlay={(e) => {
                  // Ensure audio is enabled when video can play
                  const video = e.currentTarget;
                  video.muted = false;
                  if (video.volume === 0) {
                    video.volume = 1.0;
                  }
                }}
                onClick={(e) => {
                  // Ensure audio is enabled on user click (user interaction)
                  const video = e.currentTarget as HTMLVideoElement;
                  video.muted = false;
                  video.volume = 1.0;
                  
                  // This user interaction should allow audio to play
                  console.log('[BookingChat] Video clicked - audio should be enabled');
                }}
                onLoadedData={(e) => {
                  // Ensure audio is enabled when data loads
                  const video = e.currentTarget;
                  video.muted = false;
                  video.volume = 1.0;
                }}
                onError={(e) => {
                  console.error('[BookingChat] Video failed to load:', attachment.fileUrl);
                  const video = e.currentTarget;
                  console.error('Video error details:', {
                    error: video.error,
                    errorCode: video.error?.code,
                    errorMessage: video.error?.message
                  });
                }}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '400px',
                  borderRadius: '8px',
                  backgroundColor: '#000'
                }}
              >
                <source src={attachment.fileUrl} type={attachment.mimeType || 'video/mp4'} />
                Your browser does not support the video tag.
              </video>
              {attachment.fileName && (
                <div className={styles.attachmentFileName}>{attachment.fileName}</div>
              )}
              {attachment.fileSize && (
                <div className={styles.attachmentFileSize}>
                  {this.formatFileSize(attachment.fileSize)}
                </div>
              )}
            </div>
          </div>
        );

      case 'file':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <a 
              href={attachment.fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.attachmentFile}
            >
              <File size={20} />
              <div className={styles.attachmentFileInfo}>
                <div className={styles.attachmentFileName}>
                  {attachment.fileName || 'Download File'}
                </div>
                {attachment.fileSize && (
                  <div className={styles.attachmentFileSize}>
                    {this.formatFileSize(attachment.fileSize)}
                  </div>
                )}
              </div>
            </a>
          </div>
        );

      case 'youtube':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <div className={styles.attachmentYoutube}>
              {attachment.metadata?.embedUrl ? (
                <iframe
                  src={attachment.metadata.embedUrl}
                  width="100%"
                  height="315"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube video"
                />
              ) : (
                <a 
                  href={attachment.linkUrl || attachment.fileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ position: 'relative', display: 'block' }}
                >
                  <img 
                    src={attachment.fileUrl || attachment.metadata?.thumbnailUrl} 
                    alt="YouTube Video" 
                    className={styles.youtubeThumbnail}
                  />
                  <div className={styles.youtubeOverlay}>
                    <span className={styles.youtubePlayButton}>▶️</span>
                    <span>Watch on YouTube</span>
                  </div>
                </a>
              )}
            </div>
          </div>
        );

      case 'facebook':
      case 'twitter':
      case 'instagram':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <a 
              href={attachment.linkUrl || attachment.fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.attachmentSocial}
            >
              <span className={styles.socialIcon}>
                {attachment.attachmentType === 'facebook' && '📘'}
                {attachment.attachmentType === 'twitter' && '🐦'}
                {attachment.attachmentType === 'instagram' && '📷'}
              </span>
              <span>
                {attachment.attachmentType.charAt(0).toUpperCase() + attachment.attachmentType.slice(1)} Link
              </span>
            </a>
          </div>
        );

      case 'link':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <a 
              href={attachment.linkUrl || attachment.fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.attachmentLink}
            >
              🔗 {attachment.linkUrl || attachment.fileUrl}
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  render() {
    const { organizerName, branchName, bookingId, onClose } = this.props;
    const { messages, inputMessage, isLoading, isSending, isUploading, organizerTyping, error, connectionStatus, chatEnabled, isLoadingStatus, replyingTo, selectedFiles } = this.state;
    // Hub timeline filter (embedded mode only): narrow to calls / orders / bill events.
    const hubFilter = this.state.hubFilter;
    const visibleMessages = (this.props.embedded && hubFilter !== 'all')
      ? messages.filter((m) => {
          const t = m.messageType || '';
          if (hubFilter === 'calls') return t === 'call_event' || t === 'call_message';
          if (hubFilter === 'orders') return t === 'order_event';
          if (hubFilter === 'bill') return t === 'payment_event';
          return true;
        })
      : messages;

    // Safety check - if no bookingId, show error
    if (!bookingId) {
      return (
        <div className={`ui-scope ${styles.bookingChatContainer}`}>
          <div className={styles.chatHeader}>
            <div className={styles.chatHeaderInfo}>
              <h3>Chat Error</h3>
            </div>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className={styles.chatMessages}>
            <div className={styles.emptyChat}>
              <p>Booking ID is missing. Cannot initialize chat.</p>
            </div>
          </div>
        </div>
      );
    }

    // Show loading state while checking chat status
    if (isLoadingStatus) {
      return (
        <div className={`ui-scope ${styles.bookingChatContainer}`}>
          <div className={styles.chatHeader}>
            <div className={styles.chatHeaderInfo}>
              <h3>Chat with {organizerName}</h3>
            </div>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className={styles.chatMessages}>
            <div className={styles.emptyChat}>
              <div className={styles.loadingSpinner}></div>
              <p>Checking chat availability...</p>
            </div>
          </div>
        </div>
      );
    }

    // Show disabled state if chat is not enabled
    if (!chatEnabled) {
      return (
        <div className={`ui-scope ${styles.bookingChatContainer}`}>
          <div className={styles.chatHeader}>
            <div className={styles.chatHeaderInfo}>
              <h3>Chat with {organizerName}</h3>
            </div>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className={styles.chatMessages}>
            <div className={styles.emptyChat}>
              <p>💬 Chat Not Available</p>
              <p className={styles.emptyChatHint}>
                {error || 'Chat will be enabled once the organizer accepts your booking request.'}
              </p>
              {this.state.bookingStatus && (
                <p className={styles.emptyChatHint}>
                  Current booking status: <strong>{this.state.bookingStatus}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    console.log('[BookingChat] Rendering with:', {
      organizerName,
      branchName,
      bookingId,
      messagesCount: messages.length,
      isLoading,
      connectionStatus
    });

    return (
      <div
        className={`ui-scope ${styles.bookingChatContainer}`}
        ref={this.rootRef}
        style={this.props.embedded ? { position: 'relative', inset: 'auto', top: 'auto', left: 'auto', right: 'auto', bottom: 'auto', width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', borderRadius: 12, zIndex: 'auto' } : undefined}
      >
        <UiStyles />
        {!this.props.embedded && (
        <div className={styles.chatHeader}>
          <div className={styles.chatHeaderInfo}>
            <div className={styles.chatAvatar}>🏢</div>
            <div className={styles.chatUserInfo}>
              <h3>{organizerName}</h3>
              <div className={styles.chatHeaderMeta}>
                <span className={styles.chatSubtitle}>{branchName}</span>
                <span className={`${styles.connectionStatus} ${styles[connectionStatus]}`}>
                  {connectionStatus === 'connected' && '🟢'}
                  {connectionStatus === 'connecting' && '🟡'}
                  {connectionStatus === 'disconnected' && '🔴'}
                  {connectionStatus === 'error' && '❌'}
                </span>
              </div>
            </div>
          </div>
          <div className={styles.chatHeaderActions}>
            <button 
              className={styles.viewBookingsButton}
              onClick={() => {
                window.location.href = '/customer/bookings';
              }}
              title="View My Bookings"
            >
              📅
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>
        )}

        {/* Hub filter chips (table timeline: All / Calls / Orders / Bill). */}
        {this.props.embedded && (
          <div style={{ display: 'flex', gap: 6, padding: '8px 12px 0', flexWrap: 'wrap' }}>
            {([['all', 'All'], ['calls', '🔔 Calls'], ['orders', '🍽️ Orders'], ['bill', '🧾 Bill']] as [HubFilter, string][]).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => this.setState({ hubFilter: key })}
                style={{
                  fontSize: '0.74rem', fontWeight: 700, padding: '4px 11px', borderRadius: 9999, cursor: 'pointer',
                  border: '1px solid ' + (this.state.hubFilter === key ? '#f97316' : 'rgba(148,163,184,0.35)'),
                  background: this.state.hubFilter === key ? '#f97316' : 'transparent',
                  color: this.state.hubFilter === key ? '#0b0b0b' : '#94a3b8',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className={styles.errorBanner}>
            {error}
          </div>
        )}

        {this.state.translationAvailable && (
          <div style={{ padding: '8px 12px 0' }}>
            <TranslateBar enabled={this.state.translationAvailable} convId={this.props.bookingId} />
          </div>
        )}

        <div className={styles.chatMessages} ref={this.chatContainerRef}>
          {isLoading ? (
            <div className={styles.emptyChat}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading messages...</p>
            </div>
          ) : visibleMessages.length === 0 ? (
            <div className={styles.emptyChat}>
              {this.props.embedded && hubFilter !== 'all'
                ? <p>No {hubFilter === 'bill' ? 'payments' : hubFilter} yet</p>
                : <>
                    <p>Start a conversation with {organizerName}</p>
                    <p className={styles.emptyChatHint}>Ask about your booking, order food, or request assistance</p>
                  </>}
            </div>
          ) : (
            <>
              {visibleMessages.map(message => (
                <div
                  key={message.id}
                  data-message-id={message.id}
                  className={`${styles.messageBubble} ${
                    (['call_event', 'order_event', 'payment_event', 'song_event'].includes(message.messageType || '') || message.senderType === 'system')
                      ? styles.messageSystem
                      : (message.senderType === 'customer' ? styles.messageCustomer : styles.messageOrganizer)
                  } ${
                    message.id.startsWith('temp-') ? styles.messagePending : ''
                  } ${message.status === 'failed' ? styles.messageFailed : ''}`}
                >
                  {message.senderType !== 'system' && !['call_event', 'order_event', 'payment_event', 'song_event'].includes(message.messageType || '') && message.senderType !== 'customer' && (
                    <div className={styles.messageSender}>{message.senderName}</div>
                  )}
                  
                  {/* Reply Preview */}
                  {message.replyToMessage && (
                    <div 
                      className={styles.replyPreview}
                      onClick={() => this.scrollToMessage(message.replyToMessage!.id)}
                      title="Click to view original message"
                    >
                      <div className={styles.replyPreviewContent}>
                        <div className={styles.replyPreviewSender}>
                          {message.replyToMessage.senderType === 'customer' ? 'You' : organizerName}
                        </div>
                        <div className={styles.replyPreviewText}>
                          {message.replyToMessage.messageText.length > 50
                            ? `${message.replyToMessage.messageText.substring(0, 50)}...`
                            : message.replyToMessage.messageText}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className={styles.messageText}>{message.message}</div>

                  {/* Auto-translate INCOMING (staff) messages into the viewer's language. */}
                  <TranslateInline text={message.message} enabled={this.state.translationAvailable} branchId={this.props.branchId || this.state.translationBranchId} isIncoming={message.senderType !== 'customer'} convId={this.props.bookingId} sourceLang={message.sourceLang} />

                  {/* Attachments */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className={styles.messageAttachments}>
                      {message.attachments.map(attachment => this.renderAttachment(attachment))}
                    </div>
                  )}
                  
                  <div className={styles.messageFooter}>
                    <div className={styles.messageTime}>{this.formatTime(message.timestamp)}</div>
                    {message.senderType === 'customer' && (
                      <div className={styles.messageStatus}>
                        {message.status === 'failed' ? (
                          <button
                            type="button"
                            className={styles.retryButton}
                            onClick={() => this.handleRetryMessage(message)}
                            title="Failed to send. Tap to retry"
                          >
                            ↻ Retry
                          </button>
                        ) : message.id.startsWith('temp-') ? '⏳' : message.read ? '✓✓' : '✓'}
                      </div>
                    )}
                    {/* Reply button - only show on organizer messages */}
                    {message.senderType === 'organizer' && (
                      <button
                        className={styles.replyButton}
                        onClick={() => this.handleReplyClick(message)}
                        title="Reply to this message"
                      >
                        <Reply size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {organizerTyping && (
                <div className={styles.typingIndicator}>
                  <div className={styles.typingDots}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className={styles.typingText}>{organizerName} is typing...</span>
                </div>
              )}
            </>
          )}
          <div ref={this.messagesEndRef} />
        </div>

        {/* Reply Indicator */}
        {replyingTo && (
          <div className={styles.replyIndicator}>
            <div className={styles.replyIndicatorContent}>
              <div className={styles.replyIndicatorInfo}>
                <span className={styles.replyLabel}>Replying to:</span>
                <span className={styles.replyText}>
                  {replyingTo.message.length > 50
                    ? `${replyingTo.message.substring(0, 50)}...`
                    : replyingTo.message}
                </span>
              </div>
              <button
                className={styles.cancelReplyButton}
                onClick={this.handleCancelReply}
                title="Cancel reply"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className={styles.selectedFilesPreview}>
            <div className={styles.selectedFilesHeader}>
              <span className={styles.selectedFilesCount}>
                {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
              </span>
              <button
                className={styles.clearAllFilesButton}
                onClick={() => {
                  this.setState({ selectedFiles: [] });
                  if (this.fileInputRef.current) {
                    this.fileInputRef.current.value = '';
                  }
                }}
                title="Clear all files"
              >
                Clear All
              </button>
            </div>
            <div className={styles.selectedFilesList}>
              {selectedFiles.map((file, index) => (
                <div key={index} className={styles.selectedFileItem}>
                  <div className={styles.selectedFileIcon}>
                    {this.getFileIcon(file)}
                  </div>
                  <div className={styles.selectedFileInfo}>
                    <div className={styles.selectedFileName}>{file.name}</div>
                    <div className={styles.selectedFileSize}>{this.formatFileSize(file.size)}</div>
                  </div>
                  <button
                    className={styles.removeFileButton}
                    onClick={() => this.handleRemoveFile(index)}
                    title="Remove file"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hub action toolbar — launch the real Call / Order / Menu / Bill panels. */}
        {this.props.hubActions && (
          <div style={{ display: 'flex', gap: 6, padding: '8px 10px 0', flexWrap: 'wrap' }}>
            {this.props.hubActions.onCall && (
              <button type="button" onClick={this.props.hubActions.onCall} style={hubBtn('#ef4444')}>
                🔔 Call{(this.props.hubActions.callUnread || 0) > 0 ? <span style={hubBadge}>{this.props.hubActions.callUnread}</span> : null}
              </button>
            )}
            {this.props.hubActions.onOrder && <button type="button" onClick={this.props.hubActions.onOrder} style={hubBtn('#f97316')}>🍽️ Order</button>}
            {this.props.hubActions.onMenu && <button type="button" onClick={this.props.hubActions.onMenu} style={hubBtn('#3b82f6')}>📖 {this.props.hubActions.menuLabel || 'Menu'}</button>}
            {this.props.hubActions.onBill && <button type="button" onClick={this.props.hubActions.onBill} style={hubBtn('#10b981')}>🧾 Bill</button>}
          </div>
        )}

        <form onSubmit={this.handleSendMessage} className={styles.chatInput}>
          {/* Hidden file input */}
          <input
            ref={this.fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            onChange={this.handleFileSelect}
            style={{ display: 'none' }}
          />
          
          {/* Attach button */}
          <button
            type="button"
            className={styles.attachButton}
            onClick={this.handleAttachClick}
            title="Attach files"
            disabled={isSending || connectionStatus === 'disconnected' || !chatEnabled || selectedFiles.length >= 10}
          >
            <Paperclip size={20} />
          </button>
          
          <input
            type="text"
            value={inputMessage}
            onChange={this.handleInputChange}
            placeholder={replyingTo ? `Reply to: ${replyingTo.message.substring(0, 30)}...` : 'Type your message...'}
            className={styles.inputField}
            disabled={isSending || connectionStatus === 'disconnected' || !chatEnabled}
          />
          {/* 🎤 Speak in your selected language — no keyboard setup needed on mobile web. */}
          <VoiceInput
            lang={getInputLang(this.props.bookingId)}
            baseText={inputMessage}
            onText={(t) => this.setState({ inputMessage: t })}
            disabled={isSending || connectionStatus === 'disconnected' || !chatEnabled}
          />
          <button
            type="submit"
            className={styles.sendButton} 
            disabled={(!inputMessage.trim() && selectedFiles.length === 0) || isSending || isUploading || connectionStatus === 'disconnected' || !chatEnabled}
            title={!chatEnabled ? 'Chat is not available' : connectionStatus === 'disconnected' ? 'Not connected' : isUploading ? 'Uploading files...' : 'Send message'}
          >
            {(isSending || isUploading) ? (
              <div className={styles.sendSpinner}></div>
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
      </div>
    );
  }
}

// Hub toolbar button style (accent-tinted pill).
function hubBtn(accent: string): React.CSSProperties {
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    flex: '0 0 auto',
    padding: '7px 13px',
    borderRadius: 9999,
    border: `1px solid ${accent}66`,
    background: `${accent}1f`,
    color: accent,
    fontWeight: 800,
    fontSize: '0.82rem',
    cursor: 'pointer',
  };
}
const hubBadge: React.CSSProperties = { marginLeft: 3, background: '#ef4444', color: '#fff', borderRadius: 9999, padding: '0 6px', fontSize: '0.68rem', fontWeight: 900 };

export default BookingChat;

